import {
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import {
  Chat,
  ChatInput,
  ChatStatus,
  cn,
  EMessageType,
  Flex,
  Spinner,
  toast,
  type IChatInputChip,
} from '@kognitos/lattice';
import type { TUploadedFile } from '@/shared/types';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import type { TResolvedFile } from '@/modules/outputs/types';

import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { useQuillChatAttachments, useQuillThreadMessages } from './hooks';
import { useFileUpload } from '@/shared/hooks/useFileUpload';
import { usePersistedDraftPrompt } from '@/shared/hooks/use-persisted-draft-prompt';
import {
  foldSelectionSnippets,
  useSelectionContext,
} from '@/shared/hooks/use-selection-context';
import { captureException } from '@/modules/sentry';
import { errorHandler } from '@/shared/utils/error-handler';
import { NonThinkingSpinner } from '@/shared/components/NonThinkingSpinner';
import { QuillThreadSelector } from './components';
import { ImportChecklistCard } from '../ImportChecklistCard';
import { type IQuillChatProps } from './types';
import {
  convertFileAndMessageToQuillMessage,
  convertStringToQuillMessage,
} from './utils';
import { useBuilderProgress } from '@/modules/automation-run/hooks/useBuilderProgress';
function QuillChat({
  automationId,
  className,
  headerLeading,
  initialMessage,
  initialAttachments,
  showImportChecklist = false,
}: IQuillChatProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  // Persist the unsent prompt so navigating away from the editor and back keeps
  // what the user typed. Scoped by automation id into the single editor slot.
  const [inputValue, setInputValue] = usePersistedDraftPrompt(automationId);
  const [selectedFile, setSelectedFile] = useState<TResolvedFile | null>(null);
  // Shared, cross-component store for "Select"-ed preview snippets and
  // step-added context chips, scoped by automationId — mirrors RainChatView's
  // use of the same hook (scoped there by rainThreadId instead, since a Rain
  // automation can switch between multiple threads).
  const {
    snippets: selectionSnippets,
    removeSnippet: removeSelectionSnippet,
    clear: clearSelectionSnippets,
  } = useSelectionContext(automationId);
  const chips = useMemo<IChatInputChip[]>(
    () =>
      selectionSnippets.map(snippet => ({
        id: snippet.id,
        label: snippet.text,
      })),
    [selectionSnippets],
  );
  const {
    value: showResend,
    setTrue: setShowResendTrue,
    setFalse: setShowResendFalse,
  } = useBoolean();

  const handleFileClick = useCallback((fileResource: string) => {
    // Convert file resource to TResolvedFile format
    const fileValue: TResolvedFile = {
      type: 'file-remote',
      fileUrl: fileResource,
    };
    setSelectedFile(fileValue);
  }, []);

  const {
    messages,
    isSendingMessage,
    isLongRunningSendRequest,
    isProcessingMessage,
    stopListeningToThread,
    sendMessageToThread,
    loadingPreviousMessages,
    isFetchingMorePreviousMessages,
    loadingThreads,
    stopProcessingPreviousMessages,
    fetchPreviousPage,
    hasMorePreviousMessages,
    scrollContainerRef,
    threads,
    selectedThreadId,
    selectThread,
    createNewThread,
    renameThread,
    isCreatingThread,
  } = useQuillThreadMessages({
    automationId,
    initialMessage: initialAttachments?.length
      ? convertFileAndMessageToQuillMessage(
          initialAttachments,
          initialMessage ?? '',
        )
      : initialMessage
        ? convertStringToQuillMessage(initialMessage)
        : undefined,
    onFileClick: handleFileClick,
    onSuggestedPromptClick: (prompt, sendMessageToThread) => {
      sendMessageToThread({
        message: convertStringToQuillMessage(prompt),
        action: 'sendSuggestedPrompt',
      });
    },
  });

  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Use infinite scroll hook to detect when top ref is visible
  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: loadingPreviousMessages,
    hasNextPage: hasMorePreviousMessages && !loadingPreviousMessages,
    onLoadMore: fetchPreviousPage,
    rootMargin: '0px 0px 0px 0px',
    disabled:
      !hasMorePreviousMessages || loadingPreviousMessages || !fetchPreviousPage,
  });

  const {
    attachments,
    handleFilesAdd,
    handleAttachmentRemove,
    clearAttachments,
    fileObjects,
  } = useQuillChatAttachments();

  // Setup scroll container ref for preserving scroll position
  // eslint-disable-next-line no-restricted-syntax -- reason: Attach ref to scroll container requires imperative API
  useEffect(() => {
    if (!chatContainerRef.current) {
      return;
    }

    // Find the scroll viewport within the Chat component
    const scrollArea = chatContainerRef.current.querySelector(
      '[data-slot="scroll-area-viewport"]',
    ) as HTMLElement | null;

    if (!scrollArea) {
      return;
    }

    // Set the scroll container ref for preserving scroll position
    scrollContainerRef.current = scrollArea;
    // Attach rootRef to the scroll container for infinite scroll
    rootRef(scrollArea);

    return () => {
      scrollContainerRef.current = null;
    };
  }, [rootRef, scrollContainerRef]);

  // Cleanup stream when component unmounts
  // Note: Stream is now started via onPreviousMessagesLoaded callback after previous messages are loaded
  // eslint-disable-next-line no-restricted-syntax -- reason: Cleanup stream connection on unmount
  useEffect(() => {
    return () => {
      stopListeningToThread();
    };
  }, [stopListeningToThread]);

  const { uploadFiles, isUploading } = useFileUpload({
    onSuccess: async (files: TUploadedFile[]) => {
      try {
        const fileUrls = files.map(file => file.resourceName);
        const fileUploadMessage = convertFileAndMessageToQuillMessage(
          fileUrls,
          foldSelectionSnippets(inputValue, selectionSnippets),
        );
        // Wait for first message acknowledgment before clearing input
        await sendMessageToThread({
          message: fileUploadMessage,
          action: 'sendFileMessage',
        });
        setInputValue('');
        clearAttachments();
        clearSelectionSnippets();
      } catch {
        toast.error(tAutomation('automation.quillChat.error.sendMessage'));
        setShowResendTrue();
      }
    },
  });

  const handleSend = async () => {
    if (!attachments.length) {
      try {
        // Wait for first message acknowledgment before clearing input
        await sendMessageToThread({
          message: convertStringToQuillMessage(
            foldSelectionSnippets(inputValue, selectionSnippets),
          ),
          action: 'sendTextMessage',
        });
        setInputValue('');
        clearAttachments();
        clearSelectionSnippets();
      } catch {
        toast.error(tAutomation('automation.quillChat.error.sendMessage'));
        setShowResendTrue();
      }
    } else {
      const files = Array.from(fileObjects.values());
      try {
        await uploadFiles(files);
      } catch (error) {
        // 403 → show access-denied toast; anything else → toast + capture
        errorHandler(error, () => {
          toast.error(tAutomation('automation.quillChat.error.uploadFiles'));
          captureException(error, {
            tags: { component: 'QuillChat', action: 'uploadFiles' },
          });
        });
      }
    }
  };

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) {
        handleFilesAdd(files);
      }
    },
    [handleFilesAdd],
  );

  // Inject synthetic thinking messages during gaps when the agent is processing
  const syntheticThinkingMessages = useBuilderProgress(
    isProcessingMessage || isSendingMessage,
    messages,
  );

  const messagesWithProgress = useMemo(
    () => [...messages, ...syntheticThinkingMessages],
    [messages, syntheticThinkingMessages],
  );

  const lastMessage = messages[messages.length - 1];
  const showLocalLoading =
    isProcessingMessage &&
    !isSendingMessage &&
    messages.length > 0 &&
    lastMessage.type !== EMessageType.THINKING;

  const isAwaitingInitialMessage =
    Boolean(initialMessage) &&
    messages.length === 0 &&
    !isSendingMessage &&
    !isProcessingMessage;

  const shouldShowThinkingIndicator =
    (showLocalLoading || isLongRunningSendRequest) &&
    syntheticThinkingMessages.length === 0;

  const threadSelector = (
    <QuillThreadSelector
      threads={threads}
      selectedThreadId={selectedThreadId}
      isLoading={loadingThreads}
      isCreating={isCreatingThread}
      onSelectThread={selectThread}
      onCreateThread={createNewThread}
      onRenameThread={renameThread}
    />
  );

  return (
    <Flex
      direction="column"
      className={cn(
        'mx-auto h-full pb-8',
        'transition-all duration-300 ease-in-out overflow-hidden',
        className,
      )}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {showImportChecklist && (
        <ImportChecklistCard automationId={automationId} />
      )}
      {/* `headerLeading` only wraps the selector in a row beside the control;
          the selector itself is the same either way. */}
      {headerLeading ? (
        <Flex align="center" width="full" className="shrink-0">
          <Flex align="center" className="shrink-0 pl-2">
            {headerLeading}
          </Flex>
          <Flex direction="column" align="stretch" className="min-w-0 flex-1">
            {threadSelector}
          </Flex>
        </Flex>
      ) : (
        threadSelector
      )}
      <Chat
        ref={chatContainerRef}
        className="[&_[data-slot=scroll-area-scrollbar][data-orientation=horizontal]]:hidden"
        showTimestamp
        showGradient={false}
        widgetsDisabled={isUploading || isProcessingMessage || isSendingMessage}
        messages={messagesWithProgress}
        emptyStateDescription={tAutomation(
          'automation.quillChat.emptyState.description',
        )}
        // Lattice overrides: ChatThinkingMessage renders as a direct container
        // child (no <ChatMessage> padding), so pl-2 realigns it; and its slot
        // children are intrinsically w-auto, so w-full stretches them to width.
        // Stopgap: the inner messages wrapper (chat-thinking-messages) has no
        // data-slot, so pl-2 targets its data-testid; switch once lattice adds one.
        chatContainerClassName="min-w-0 pb-3 px-3 [&_[data-testid=chat-thinking-messages]]:pl-2 [&_[data-slot=chat-thinking-message]>*]:w-full"
        isLoading={
          loadingPreviousMessages || loadingThreads || isAwaitingInitialMessage
        }
        topComponent={
          hasMorePreviousMessages ? (
            <div ref={infiniteRef} className="w-full flex justify-center py-4">
              {isFetchingMorePreviousMessages && <Spinner size="sm" />}
            </div>
          ) : null
        }
      >
        {shouldShowThinkingIndicator && <NonThinkingSpinner />}
        {showResend && (
          <ChatStatus
            state="error"
            text={tAutomation('automation.quillChat.error.sendMessage')}
            action={{
              label: tCommon('common.action.retry'),
              onClick: () => {
                setShowResendFalse();
                handleSend();
              },
            }}
          />
        )}
        <ChatInput
          className="-m-1 px-1 pb-2 pt-3"
          chips={chips}
          onChipRemove={removeSelectionSnippet}
          attachments={attachments}
          onAddAttachments={handleFilesAdd}
          onAttachmentRemove={handleAttachmentRemove}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onSend={handleSend}
          disabled={isUploading || isProcessingMessage || isSendingMessage}
          isLoading={isProcessingMessage}
          allowFilePaste
          loadingMode={isSendingMessage ? 'loader' : 'stop'}
          tooltips={{
            stop: {
              content: tAutomation('automation.quillChat.action.stopResponse'),
              variant: 'primary',
            },
          }}
          onStop={
            isSendingMessage
              ? undefined
              : () => {
                  stopProcessingPreviousMessages();
                }
          }
        />
      </Chat>
      <PreviewLayover
        isOpen={selectedFile !== null}
        outputName=""
        outputValue={selectedFile}
        onClose={() => setSelectedFile(null)}
      />
    </Flex>
  );
}

export default QuillChat;
